import { useMemo, useState } from 'react';
import { Plus, Server, Box, Settings, Layers, Thermometer } from 'lucide-react';
import { clsx } from 'clsx';
import {
  DatacenterNode,
  FormFactor,
  ContainerConfig,
  ContainerSize,
  ContainerInsulation,
  calculateContainerStats,
} from '../../types/configurator';
import { Button } from '../ui';
import { ContainerFloorPlan } from './ContainerFloorPlan';
import { RackDetailPanel } from './RackDetailPanel';
import { InfrastructurePanel } from './InfrastructurePanel';
import { ContainerStatsBar } from './ContainerStatsBar';
import {
  INFRASTRUCTURE_CATALOG,
  getInfrastructureCategories,
  CONTAINER_INSULATION_PRESETS,
  calculateInsulationCost,
} from '../../data/infrastructure';

interface ContainerViewProps {
  formFactor: FormFactor;
  nodes: DatacenterNode[];
  containerConfig: ContainerConfig | undefined;
  onNodeClick?: (nodeId: string) => void;
  selectedNodeId?: string;
  // Container management
  onInitializeContainer: (size: ContainerSize) => void;
  onSetContainerSize: (size: ContainerSize) => void;
  onAddRack: (type: 'rack-18' | 'rack-42') => void;
  onUpdateRack: (rackId: string, updates: Record<string, unknown>) => void;
  onRemoveRack: (rackId: string) => void;
  onMoveRack: (rackId: string, position: { x: number; y: number }) => void;
  onAssignNodeToRack: (nodeId: string, rackId: string) => void;
  onUnassignNodeFromRack: (nodeId: string) => void;
  onAddInfrastructure: (catalogId: string) => void;
  onUpdateInfrastructure: (id: string, updates: Record<string, unknown>) => void;
  onRemoveInfrastructure: (id: string) => void;
  onMoveInfrastructure: (id: string, position: { x: number; y: number }) => void;
  onSetInsulation?: (insulation: ContainerInsulation) => void;
}

export function ContainerView({
  formFactor: _formFactor,
  nodes,
  containerConfig,
  onNodeClick: _onNodeClick,
  selectedNodeId: _selectedNodeId,
  onInitializeContainer,
  onSetContainerSize,
  onAddRack,
  onUpdateRack,
  onRemoveRack,
  onMoveRack,
  onAssignNodeToRack,
  onUnassignNodeFromRack,
  onAddInfrastructure,
  onUpdateInfrastructure,
  onRemoveInfrastructure,
  onMoveInfrastructure,
  onSetInsulation,
}: ContainerViewProps) {
  const [selectedRackId, setSelectedRackId] = useState<string | undefined>();
  const [selectedInfraId, setSelectedInfraId] = useState<string | undefined>();
  const [showAddInfraMenu, setShowAddInfraMenu] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showInsulationMenu, setShowInsulationMenu] = useState(false);

  // Initialize container if not exists
  if (!containerConfig) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 text-center">
        <Box className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Container Not Configured</h3>
        <p className="mt-2 text-sm text-gray-500">
          Choose a container size to get started with your deployment layout.
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <Button onClick={() => onInitializeContainer('20ft')}>
            <Box className="mr-2 h-4 w-4" />
            20ft Container
          </Button>
          <Button variant="outline" onClick={() => onInitializeContainer('40ft')}>
            <Box className="mr-2 h-4 w-4" />
            40ft Container
          </Button>
        </div>
      </div>
    );
  }

  // Calculate stats
  const stats = useMemo(
    () => calculateContainerStats(containerConfig, nodes),
    [containerConfig, nodes]
  );

  // Get selected rack details
  const selectedRack = containerConfig.racks.find((r) => r.id === selectedRackId);
  const selectedRackNodes = selectedRack
    ? nodes.filter((n) => selectedRack.nodes.includes(n.id))
    : [];

  // Get selected infrastructure
  const selectedInfra = containerConfig.infrastructure.find((i) => i.id === selectedInfraId);

  // Infrastructure categories
  const infraCategories = getInfrastructureCategories();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Box className="h-5 w-5 text-gray-500" />
            <select
              value={containerConfig.size}
              onChange={(e) => onSetContainerSize(e.target.value as ContainerSize)}
              className="rounded border border-gray-200 px-2 py-1 text-sm font-medium"
            >
              <option value="20ft">20ft Container</option>
              <option value="40ft">40ft Container</option>
            </select>
          </div>

          <span className="text-sm text-gray-500">
            {containerConfig.racks.length} rack{containerConfig.racks.length !== 1 ? 's' : ''} |{' '}
            {containerConfig.infrastructure.length} infrastructure unit
            {containerConfig.infrastructure.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Add Rack */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAddRack('rack-42')}
              title="Add 42U rack"
            >
              <Plus className="mr-1 h-4 w-4" />
              <Server className="h-4 w-4" />
              <span className="ml-1">42U</span>
            </Button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onAddRack('rack-18')}
            title="Add 18U rack"
          >
            <Plus className="mr-1 h-4 w-4" />
            <Server className="h-4 w-4" />
            <span className="ml-1">18U</span>
          </Button>

          {/* Insulation Settings */}
          {onSetInsulation && (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInsulationMenu(!showInsulationMenu)}
                className={clsx(
                  containerConfig.insulation?.enabled && 'border-orange-400 bg-orange-50'
                )}
              >
                <Layers className="mr-1 h-4 w-4" />
                <span>Insulation</span>
                {containerConfig.insulation?.enabled && (
                  <span className="ml-1 text-xs text-orange-600">
                    ({containerConfig.insulation.type})
                  </span>
                )}
              </Button>

              {showInsulationMenu && (
                <div className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg">
                  <div className="border-b border-gray-200 p-3">
                    <h4 className="flex items-center gap-2 text-sm font-medium">
                      <Thermometer className="h-4 w-4 text-orange-500" />
                      Container Insulation
                    </h4>
                    <p className="mt-1 text-xs text-gray-500">
                      Select insulation package for thermal and acoustic protection
                    </p>
                  </div>
                  <div className="p-2">
                    {Object.entries(CONTAINER_INSULATION_PRESETS).map(([key, preset]) => {
                      const cost = calculateInsulationCost(preset.type, containerConfig.size);
                      const isSelected = containerConfig.insulation?.type === preset.type;
                      return (
                        <button
                          key={key}
                          onClick={() => {
                            onSetInsulation({
                              enabled: preset.type !== 'none',
                              type: preset.type,
                              wallRValue: preset.wallRValue,
                              ceilingRValue: preset.ceilingRValue,
                              floorRValue: preset.floorRValue,
                              soundReductionDb: preset.soundReductionDb,
                              estimatedCost: cost,
                            });
                            setShowInsulationMenu(false);
                          }}
                          className={clsx(
                            'flex w-full items-center justify-between rounded px-3 py-2 text-left text-sm',
                            isSelected ? 'bg-orange-100' : 'hover:bg-gray-50'
                          )}
                        >
                          <div>
                            <span className="font-medium capitalize">{preset.type}</span>
                            {preset.wallRValue && (
                              <span className="ml-2 text-xs text-gray-500">
                                R-{preset.wallRValue}
                                {preset.soundReductionDb && ` / ${preset.soundReductionDb}dB`}
                              </span>
                            )}
                          </div>
                          <span className="text-xs font-medium text-gray-600">
                            {cost > 0 ? `$${cost.toLocaleString()}` : 'Free'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Add Infrastructure */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddInfraMenu(!showAddInfraMenu)}
            >
              <Plus className="mr-1 h-4 w-4" />
              <Settings className="h-4 w-4" />
              <span className="ml-1">Equipment</span>
            </Button>

            {showAddInfraMenu && (
              <div className="absolute right-0 top-full z-50 mt-1 w-80 rounded-lg border border-gray-200 bg-white shadow-lg">
                {/* Category filter tabs */}
                <div className="border-b border-gray-200 p-2">
                  <div className="flex flex-wrap gap-1">
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                        selectedCategory === null
                          ? 'bg-hermit-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      All
                    </button>
                    {infraCategories.map((category) => (
                      <button
                        key={category.type}
                        onClick={() => setSelectedCategory(category.type)}
                        className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                          selectedCategory === category.type
                            ? 'bg-hermit-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {category.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Items list */}
                <div className="max-h-64 overflow-auto p-2">
                  {infraCategories
                    .filter((category) => !selectedCategory || category.type === selectedCategory)
                    .map((category) => {
                      const items = INFRASTRUCTURE_CATALOG.filter(
                        (i) => i.type === category.type
                      );
                      if (items.length === 0) return null;

                      return (
                        <div key={category.type} className="mb-2">
                          {!selectedCategory && (
                            <div className="px-2 py-1 text-xs font-medium uppercase text-gray-500">
                              {category.label}
                            </div>
                          )}
                          {items.map((item) => (
                            <button
                              key={item.id}
                              onClick={() => {
                                onAddInfrastructure(item.id);
                                setShowAddInfraMenu(false);
                              }}
                              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100"
                            >
                              <div>
                                <span className="block">{item.name}</span>
                                <span className="block text-[10px] text-gray-400">
                                  {item.specs.widthMm}×{item.specs.depthMm}mm
                                </span>
                              </div>
                              <span className="text-xs font-medium text-gray-500">
                                ${item.specs.price}
                              </span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex gap-4">
        {/* Floor plan */}
        <div className="flex-1">
          <ContainerFloorPlan
            containerSize={containerConfig.size}
            racks={containerConfig.racks}
            infrastructure={containerConfig.infrastructure}
            selectedRackId={selectedRackId}
            selectedInfraId={selectedInfraId}
            onRackSelect={(rackId) => {
              setSelectedRackId(rackId);
              setSelectedInfraId(undefined);
            }}
            onRackMove={onMoveRack}
            onInfrastructureSelect={(infraId) => {
              setSelectedInfraId(infraId);
              setSelectedRackId(undefined);
            }}
            onInfrastructureMove={onMoveInfrastructure}
            showGrid
          />
        </div>

        {/* Detail panel */}
        <div className="w-72">
          {selectedRack ? (
            <RackDetailPanel
              rack={selectedRack}
              nodes={selectedRackNodes}
              infrastructure={containerConfig.infrastructure}
              allNodes={nodes}
              allRacks={containerConfig.racks}
              onRackUpdate={(updates) => onUpdateRack(selectedRack.id, updates)}
              onRackRemove={() => {
                onRemoveRack(selectedRack.id);
                setSelectedRackId(undefined);
              }}
              onAssignNode={(nodeId) => onAssignNodeToRack(nodeId, selectedRack.id)}
              onUnassignNode={onUnassignNodeFromRack}
            />
          ) : selectedInfra ? (
            <InfrastructurePanel
              infrastructure={selectedInfra}
              racks={containerConfig.racks}
              onUpdate={(updates) => onUpdateInfrastructure(selectedInfra.id, updates)}
              onRemove={() => {
                onRemoveInfrastructure(selectedInfra.id);
                setSelectedInfraId(undefined);
              }}
              onMountToRack={(rackId) =>
                onUpdateInfrastructure(selectedInfra.id, { rackId })
              }
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Server className="mx-auto h-8 w-8 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                Select a rack or equipment to view details
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <ContainerStatsBar stats={stats} />

      {/* Click outside to close menus */}
      {(showAddInfraMenu || showInsulationMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowAddInfraMenu(false);
            setShowInsulationMenu(false);
          }}
        />
      )}
    </div>
  );
}
